// Package imports:
import React, { useMemo, useState } from 'react';
import cx from 'classnames';
// Component imports:
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import DelayOrClosedLabel from '../DelayOrClosedLabel/DelayOrClosedLabel';
import DisplayBox from '../DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, convertToPercentage } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IApiLmdStatistics } from '../../types/HlutabrefTypes';
import { IApiLmdSnapshot, IApiLmdTradables } from '../../types/SkuldabrefTypes';
import { IDefaultProps } from '../../types/Types';

type TimePeriod = 'intraday' | 'weeks4' | 'months3' | 'year';

type HeatMapColor = 'pos' | 'neg' | 'neutral';
interface IHeatMapData {
    symbol: string | null,
    color: HeatMapColor,
    value: number | null,
    changeValue: number | null
}

type Props = IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const HeatMap: React.FC<Props> = ({
    refreshRateMs,
    accessToken,
    ...props
}) => {
    const [statistics] = useApiLmdData<IApiLmdStatistics[]>(
        '/v1/market_data/v1/exchanges/XICE/markets/ICECB/tradables/*/statistics',
        accessToken,
        refreshRateMs
    );
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('intraday');
    const [activeBonds] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/exchanges/XICE/active/bonds/delayed_snapshot',
        accessToken,
        refreshRateMs
    );
    const [staticDataBonds] = useApiLmdData<IApiLmdTradables[]>(
        '/v1/static_data/v1/exchanges/XICE/tradables/*?skip_expired=true&bond=true&share=false',
        accessToken,
        refreshRateMs
    );

    const staticEval = (symbol: string | null) => {
        const { data } = staticDataBonds;
        if (symbol === null || data === null) return false;
        const staticEntry = data.find(e => e.Symbol === symbol);
        const today = new Date();
        const periodStartDate = timePeriod === 'weeks4'
            ? new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
            : timePeriod === 'months3'
            ? new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())
            : timePeriod === 'year'
            ? new Date(today.getFullYear())
            : new Date();
        if (staticEntry?.ListingDate && staticEntry?.ListingDate > periodStartDate) return true;
        return undefined;
    };

    const marketEval = (stat: IApiLmdStatistics) => {
        if (timePeriod === 'intraday') return false;
        const tradeCount = stat[`${timePeriod}_trade_count`];
        if (tradeCount && tradeCount > 0) return true;
        return undefined;
    };

    const displayData = useMemo(() => {
        const newData: IHeatMapData[] = [];
        if (statistics.data === null) return [];
        if (timePeriod === 'intraday') {
            if (activeBonds.data === null) return [];
            for (let entry of activeBonds.data) {
                const changeVal = entry.last_yield_diff === null ? 0 : entry.last_yield_diff * 10000;
                newData.push({
                    symbol: entry.symbol,
                    color: changeVal === null ? 'neutral' : changeVal < 0 ? 'pos' : changeVal > 0 ? 'neg' : 'neutral',
                    value: entry.last_yield ?? 0,
                    changeValue: changeVal
                });
            }
        } else {
            if (staticDataBonds.data === null) return [];
            for (let entry of staticDataBonds.data) {
                const statsEntry = statistics.data.find(e => e.symbol === entry.Symbol);
                const static_eval = staticEval(entry.Symbol);
                const market_eval = statsEntry && marketEval(statsEntry);
                if ((static_eval !== false && market_eval) || (static_eval && market_eval !== false)) {
                    const yieldVal = statsEntry ? statsEntry[`${timePeriod}_yield`] : 0;
                    const changeVal = !statsEntry?.last_yield || yieldVal === null ? 0 : (statsEntry?.last_yield - yieldVal) * 10000;
                    newData.push({
                        symbol: entry.Symbol,
                        color: changeVal === null ? 'neutral' : changeVal < 0 ? 'pos' : changeVal > 0 ? 'neg' : 'neutral',
                        value: statsEntry?.last_yield ?? 0,
                        changeValue: changeVal
                    });
                }
            }
        }
        newData.sort((a, b) => {
            const aval = a.changeValue;
            const bval = b.changeValue;
            return aval === null && bval === null
                ? 0
                : aval === null
                ? -1
                : bval === null
                ? 1
                : aval > bval
                ? 1
                : aval < bval
                ? -1
                : 0;
        });
        return newData;
    }, [statistics, staticDataBonds, activeBonds, timePeriod]);
    const dataLength = displayData.length;

    return (
        <DisplayBox className='KCL_heat-map'
            title="Hitakort"
            statusComponent={<DelayOrClosedLabel closeType="bonds" />}
            asideComponent={
                <div className="time-period-buttons">
                    <button
                        className={cx({'is-active': timePeriod === 'intraday'})}
                        onClick={() => setTimePeriod('intraday')}
                    >
                        Í dag
                    </button>
                    <button 
                        className={cx({'is-active': timePeriod === 'weeks4'})}
                        onClick={() => setTimePeriod('weeks4')}
                    >
                        1 mán.
                    </button>
                    <button
                        className={cx({'is-active': timePeriod === 'months3'})}
                        onClick={() => setTimePeriod('months3')}
                    >
                        3 mán.
                    </button>
                    <button
                        className={cx({'is-active': timePeriod === 'year'})}
                        onClick={() => setTimePeriod('year')}
                    >
                        Þetta ár
                    </button>
                </div>
            }
            {...props}
        >
            {(statistics.error instanceof Error)
            ? <Alert type='error' headText={statistics.error.message}/>
            : (statistics === null)
            ? <Loading />
            : (activeBonds.error instanceof Error)
            ? <Alert type='error' headText={activeBonds.error.message}/>
            : (activeBonds.data === null)
            ? <Loading />
            : (
                <div className="heat-map-body">
                    <div className='heat-map-container'>
                        {displayData.map((datum, i) => {
                            return <a key={i}
                                href={`/Markadir/Skuldabref/${datum.symbol}`}
                                className={cx(
                                    "heat-map-item",
                                    datum.color,
                                    i === 0 ? 'first' : i === dataLength - 1 ? 'last' : ''
                                )}
                            >
                                <span className="symbol">{datum.symbol}</span>
                                <span className="values">
                                    {convertToPercentage(datum.value, true)} ({formatNumber(datum.changeValue, null, 0)} pkt.)
                                </span>
                            </a>
                        })}
                    </div>
                </div>
            )}
        </DisplayBox>
    );
}

export default HeatMap;